import React, {
    useContext,
    useState,
    useEffect,
    useRef,
} from 'react';
import { useNavigate, useParams } from "react-router";
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';

import { getBasketData, refetchBasketData, NotificationContext, handleError } from '../router';

import * as CONSTANTS from "../constants";
import * as QUERIES from "../query-functions";
import * as UTILS from "../utils";
import * as OPTIONS from "../option-fields";

import InstanceEventSummary from '../components/instance-event-summary';
import FilterSeatsByPrice from "./filter-seats-by-price";
import FilterSeatsByArea from "./filter-seats-by-area";
import SeatingPlan from "./seating-plan";
import LoadingIndicator from "../components/loading-indicator";
import CheckForQueue from './check-for-queue';
import CrossIcon from '../svg/cross';
import InfoCircleIcon from '../svg/info-circle';

export default function SelectSeats({ checkedForQueue, setCheckedForQueue }) {

    const {notification, setNotification} = useContext(NotificationContext);
    const { instanceId } = useParams();
    let navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: basketData, isFetching: basketDataFetching } = getBasketData();

    const [selectedBands, setSelectedBands] = useState(null);
    const [selectedArea, setSelectedArea] = useState(null);
    const [seatTicketIsPending, setSeatTicketIsPending] = useState(null);
    const [enabledFilters, setEnabledFilters] = useState([]);
    const [ticketGroup, setTicketGroup] = useState(null);
    const [ticketsTotal, setTicketsTotal] = useState(null);
    const [groupedPrices, setGroupedPrices] = useState([]);
    const [selectedSeatCount, setSelectedSeatCount] = useState(0);
    const [seatingPlanError, setSeatingPlanError] = useState(null);
    const [hasIsolatedSeats, setHasIsolatedSeats] = useState(false);
    const [maxTickets, setMaxTickets] = useState(null);

    const { data: planData } = useQuery({
        queryKey: ['planData', CONSTANTS.PLAN_ID],
        queryFn: QUERIES.getPlanData,
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    const {
        data: priceListData,
    } = useQuery({
        queryKey: ["priceListData", CONSTANTS.INSTANCE_ID],
        queryFn: () => QUERIES.getInstancePriceList(CONSTANTS.INSTANCE_ID),
        staleTime: 86400000,
        refetchOnWindowFocus: false,
    });

    const { data: seatStatusData } = useQuery({
            queryKey: ['seatStatusData', CONSTANTS.INSTANCE_ID],
            queryFn: QUERIES.getSeatData
    });
    
    // Adding/Remove tickets
    const pendingMutationsRef = useRef(0);

    // Don't refetch basket and details data until add/remove function are completed
    const trackMutation = async (mutationFn) => {
        setSeatTicketIsPending(true);
        pendingMutationsRef.current += 1;

        try {
            await mutationFn();
        } catch (e) {
            // Error already handled in onError
        } finally {
            pendingMutationsRef.current -= 1;

            if (pendingMutationsRef.current === 0) {
                refetchBasketData();
                queryClient.invalidateQueries({
                    queryKey: ['seatStatusData', CONSTANTS.INSTANCE_ID],
                    exact: true,
                });
                setSeatTicketIsPending(false);
            }
        }
    };

    const addTicket = useMutation({
        mutationFn: ({ ticketTypeId, seatId }) =>
            QUERIES.addTickets(1, ticketTypeId, seatId),
        onError: (error) => {
            if (error.errorCode === 43 && error.instanceLimits?.length) {
                setMaxTickets(error.instanceLimits[0].maxTickets);
            }
            handleError(error.message, setNotification);
        },
    });

    const removeTicket = useMutation({
        mutationFn: (seatBasketId) => QUERIES.removeTickets([seatBasketId]),
        onError: (error) => handleError(error.message, setNotification),
    });
    
    const handleAddTicket = ({ ticketTypeId, seatId }) => {
        trackMutation(() => addTicket.mutateAsync({ ticketTypeId, seatId }));
        setSelectedSeatCount(prev => prev + 1);
    };

    const handleRemoveTicket = (seatBasketId) => {
        trackMutation(() => removeTicket.mutateAsync(seatBasketId));
        setSelectedSeatCount(prev => prev - 1);
    };

    useEffect(() => {
        
        if (!basketData || !basketData?.tickets) {
            setTicketGroup(null);
            setTicketsTotal(null);
            return;
        };

        // To show in the continue button
        let tickets = UTILS.getTicketsByInstance(basketData.tickets);
        if (!tickets?.length) {
            setTicketGroup(null);
            setTicketsTotal(null);
            return;
        }

        setSelectedSeatCount(tickets.length);

        const grouped_tickets = UTILS.groupTicketsByInstance(tickets);
        const tickets_total = UTILS.getTicketsTotal(tickets);

        // Set seat info and seatData for each ticket
        grouped_tickets[0].tickets.forEach((ticket, index) => {
            if (seatStatusData) {
                let info = (seatStatusData?.info[ticket.seat.id])

                //replace + with a space
                info = info?.priceBandId.replace(/\+/g, " ") || null;
                ticket.seat.info = info;
            }

            const matchingTicket = basketData.tickets.find(
                (ticket) => ticket.seat?.name === grouped_tickets[0].seats[index].seat_name
            );
                
            if (matchingTicket) {
                grouped_tickets[0].seats[index].seatData = matchingTicket.seat
            }
        });

        setTicketGroup(grouped_tickets[0]);
        setTicketsTotal(tickets_total.toFixed(2));

    }, [basketData, seatStatusData]);

    useEffect(() => {
        if (!priceListData || !enabledFilters) return;

        const grouped = UTILS.groupPriceListByAmount(priceListData, enabledFilters);
        setGroupedPrices(grouped);
    }, [priceListData, enabledFilters]);

    useEffect(() => {
        if (maxTickets && selectedSeatCount >= maxTickets) {
            setSeatingPlanError(`You can only select a maximum of ${maxTickets} tickets for this performance.`);
        } else if (hasIsolatedSeats && !CONSTANTS.TURN_OFF_VACANCY_RESTRICTION) {
            setSeatingPlanError(`Your current selection would leave one or more seats isolated please choose more tickets or a different selection.`);
        } else {
            setSeatingPlanError(null);
        }
    }, [selectedSeatCount, hasIsolatedSeats, maxTickets]);

    // Check to see if this event needs a queue by rendering the chooseseats iframe.
    // If the iframe goes to chooseSeats.aspx load the rest of the component
    if (!checkedForQueue) {
        return (
            <div className="booking-pathway__book-online">
                <LoadingIndicator type="page" />

                <CheckForQueue setCheckedForQueue={setCheckedForQueue} />
            </div>
        )
    }

    return (
        <div className="booking-pathway__container booking-pathway__select-seat">

        <InstanceEventSummary showAlternativeDates="true" />
        
        <header className='booking-pathway__select-seat--header'>
            <h1 className='booking-pathway__header'>Select seats</h1>

            <div className='booking-pathway__filters'>

                {enabledFilters?.length ?
                    <>
                        {groupedPrices?.length ?
                            <section className="booking-pathway__filters--prices">
                                    <h2 className="booking-pathway__header">Filter by price</h2>
                                    <ul className="booking-pathway__filter-list booking-pathway__filter-list--price" role="list">
                                        {groupedPrices                                         
                                            .map(price => (
                                            <li key={price.amount}>
                                             
                                              <FilterSeatsByPrice 
                                                price={price} 
                                                selectedBands={selectedBands} 
                                                setSelectedBands={setSelectedBands}
                                                enabledFilters={enabledFilters} /> 
                                            </li>
                                          ))
                                        }
                                    </ul>
                            </section>
                        :null
                        }
                        {planData?.areas?.length && CONSTANTS.HAS_LOCATION_FILTERS ? 
                            <section className="booking-pathway__filters--areas">
                                <h2 className="booking-pathway__header">Filter by location</h2>
                                <ul className="booking-pathway__filter-list booking-pathway__filter-list--area" role="list">
                                    { planData.areas.map((area) => {
                                        if (area.type == 'Reserved' && enabledFilters.includes(area.id)) return (
                                        <li key={area.id}>
                                            <FilterSeatsByArea 
                                                area={area} 
                                                selectedArea={selectedArea} 
                                                setSelectedArea={setSelectedArea}
                                                enabledFilters={enabledFilters} /> 
                                        </li>
                                    )})}
                                </ul>
                            </section>
                        :null
                        }
                    </>
                :null
                }
            </div>
        </header>

        <SeatingPlan
            planData={planData}
            groupedPrices={groupedPrices}
            selectedBands={selectedBands}
            selectedArea={selectedArea}
            setSeatTicketIsPending={setSeatTicketIsPending}
            seatTicketIsPending={seatTicketIsPending}
            setEnabledFilters={setEnabledFilters}
            enabledFilters={enabledFilters}
            addTicket={handleAddTicket}
            removeTicket={handleRemoveTicket}
            selectedSeatCount={selectedSeatCount}
            seatingPlanError={seatingPlanError}
            setHasIsolatedSeats={setHasIsolatedSeats}
            maxTickets={maxTickets}
             />

        {ticketGroup ?
            <div className='basket-summary'>
                <h2 className="basket-summary__item--header">Your seat selection</h2>
                <ul role="list" className="basket-summary__items basket-summary__items--seats">
                    {ticketGroup.seats?.map((seat) => (
                        <li key={seat.ticket_id} className='basket-summary__item'>
                            {seat.plan_name && (
                                <span className='basket-summary__item--plan-name'>
                                    {seat.plan_name}
                                </span>
                            )}
                            {seat.seat_name && (
                                <span className='basket-summary__item--seat-name'>
                                    {seat.seat_name}
                                </span>
                            )}
                            <button type="button" onClick={() => handleRemoveTicket(seat.ticket_id)} className='btn btn--remove'>
                                <span className='sr-text'>Remove ticket</span>
                                <CrossIcon />
                            </button>
                            {seat.seatData?.info && (
                                <span className='select-ticket-type__seat-info'>
                                    <InfoCircleIcon />
                                    {seat.seatData.info}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>

                {OPTIONS.concessionMessage.text ? (
                <div
                    className="booking-pathway__info-text"
                    dangerouslySetInnerHTML={{ __html: OPTIONS.concessionMessage.text.innerHTML }}
                />
                ) : null}
            </div>
        :null}

        <div className="booking-pathway__footer">

            <button className='btn btn--primary' 
                    type="button" 
                    onClick={() => {
                        window.scrollTo(0, 0);
                        navigate(`/book-online/${instanceId}/edit-tickets`);
                    }}
                    disabled={basketDataFetching || seatTicketIsPending || !ticketsTotal || seatingPlanError}>
                        Continue
                        {basketDataFetching || seatTicketIsPending ?
                            <LoadingIndicator />
                        : ticketsTotal &&
                        ` - £${ticketsTotal}`
                        }
            </button>
            
        </div>
    </div>
    );
}